import React from "react";
//#region redux
import { useDispatch } from "react-redux";
import {
  invoiceDiscarded,
  invoiceDueDateChangedToFutureDate,
  invoiceDueDateChangedToPastDate,
  invoiceSnoozed,
} from "store/actions/invoiceActions";
//#endregion
//#region types
import { WintSuperkollAPIModel as ApiModel } from "wint-api-client/dist/types";
import { StyleEnums, DialogEnums } from "models/enums";
import { InvoiceMoreActionProps } from "models/propTypes/invoicePropTypes";
import { ACTION_SEPARATOR, MoreActionMenuItemType } from "models/propTypes/menuPropTypes";
import { IconDefinitionConstants, RouteConstants } from "utils/constants";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import { MoreActions, useCheckRelations, useSnoozeAction } from "components/common";
import CreditInvoiceSelectionForm from "../forms/creditInvoiceSelectionForm";
import InvoiceEditDueFormContainer from "../forms/editDueFormContainer";
import InvoiceReportAsPaidFormContainer from "../forms/reportAsPaidFormContainer";
import useInvoiceSendMoreActions from "./useMoreActions";
import useAssignNewOwner from "./useAssignOwner";
import { useNavigate, useLocation } from "react-router-dom";
import { useInvoiceService } from "serviceHooks";
import useInvoicePermissions from "../useInvoicePemissions";
//#endregion
import { LocalizationHelperInstance, UrlHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const InvoiceMoreActions: React.FC<InvoiceMoreActionProps> = (props) => {
  const invoiceService = useInvoiceService();
  const { invoice, reloadHandler, markAsDoneHandler, disableSuccessMessages } = props;

  const location = useLocation();
  const navigate = useNavigate();

  const dispatch = useDispatch();

  const { getIsReadonlyUser } = useCheckRelations();
  const { getIsInvoiceAdmin } = useInvoicePermissions();

  const { downloadAction, emailInvoiceAdvancedAction, printInvoiceAction } = useInvoiceSendMoreActions(
    props.invoice,
    props.reloadHandler
  );

  const cancelInvoice = () => {
    return invoiceService.deleteInvoice(props.invoice.id);
  };

  const onCancelSuccess = () => {
    markAsDoneHandler && markAsDoneHandler();
    dispatch(invoiceDiscarded(invoice.id));
  };

  const onEditPaymentActionSuccess = () => {
    props.setLoading && props.setLoading(true);
    invoiceService
      .get(invoice.id)
      .then((result) => {
        if (result.status === invoice.status) {
          reloadHandler && reloadHandler();
          return;
        }

        if (
          invoice.status === ApiModel.InvoiceStatus.Unpaid &&
          result.status === ApiModel.InvoiceStatus.OverdueReminderNotSent
        ) {
          dispatch(invoiceDueDateChangedToPastDate(invoice));
        }

        if (
          (invoice.status === ApiModel.InvoiceStatus.OverdueReminderNotSent ||
            invoice.status === ApiModel.InvoiceStatus.OverdueReminderSent) &&
          result.status === ApiModel.InvoiceStatus.Unpaid
        ) {
          dispatch(invoiceDueDateChangedToFutureDate(invoice.id));
        }

        markAsDoneHandler && markAsDoneHandler();
      })
      .finally(() => {
        props.setLoading && props.setLoading(false);
      });
  };

  const cancelAction: MoreActionMenuItemType = {
    description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.Cancel),
    icon: IconDefinitionConstants.WTimes,
    actionDialog: {
      isOpen: false,
      title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.Cancel),
      titleIcon: IconDefinitionConstants.WTimes,
      dialogType: DialogEnums.DialogType.Message,
      actions: [
        {
          text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.SaveChanges),
          onClick: cancelInvoice,
          type: StyleEnums.DisplayType.Danger,
          successMessage: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.CancelSuccessMessage),
        },
      ],
      children: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.CancelWarning),
      type: StyleEnums.DisplayType.Danger,
    },
    highlightType: StyleEnums.DisplayType.Danger,
    onActionSuccess: onCancelSuccess,
  };

  const onSnoozeSuccess = () => {
    markAsDoneHandler && markAsDoneHandler();
    dispatch(invoiceSnoozed(invoice.id));
  };

  const { snoozeAction } = useSnoozeAction(props.invoice.id, ApiModel.TodoType.Invoice, onSnoozeSuccess);

  const createRecurringAction: MoreActionMenuItemType = {
    description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.CreateRecurringAction),
    icon: IconDefinitionConstants.WRecurring,
    onClickHandler: async () => {
      navigate(
        UrlHelper.formatPath(
          `/Invoice/Create?${RouteConstants.QueryParamStrings.InvoiceTemplate}=${props.invoice.id}&${RouteConstants.QueryParamStrings.IsRecurring}=true`
        ),
        {
          state: { background: location },
        }
      );
    },
  };

  const editPaymentAction: MoreActionMenuItemType = {
    description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.EditDueDate),
    icon: IconDefinitionConstants.WMonthCalendar,
    actionDialog: {
      isOpen: false,
      title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.EditDueDate),
      subTitle: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.EditDueDateDescription),
      titleIcon: IconDefinitionConstants.WMonthCalendar,
      dialogType: DialogEnums.DialogType.Content,
      actions: [
        {
          text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.SaveChanges),
          successMessage: LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.Invoice.DueDateUpdatedSuccessMessage
          ),
        },
      ],
      maxWidth: "md",
      children: (
        <InvoiceEditDueFormContainer
          invoiceId={props.invoice.id}
          dueDate={props.invoice.dueDate}
          invoiceDate={props.invoice.postingDate}
          reminderDueDate={props.invoice.reminderDueDate}
        />
      ),
    },
    onActionSuccess: onEditPaymentActionSuccess,
  };

  const reportAsPaidAction: MoreActionMenuItemType = {
    description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.ReportAsPaid),
    icon: IconDefinitionConstants.WTransactions,
    actionDialog: {
      isOpen: false,
      title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.ReportAsPaid),
      subTitle: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.ReportAsPaidDescription),
      titleIcon: IconDefinitionConstants.WTransactions,
      dialogType: DialogEnums.DialogType.Content,
      actions: [
        {
          text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.SaveChanges),
          successMessage: LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.Invoice.ReportAsPaidSuccessMessage
          ),
        },
      ],
      maxWidth: "md",
      children: (
        <InvoiceReportAsPaidFormContainer invoiceId={props.invoice.id} paymentDate={props.invoice.paymentDate} />
      ),
    },
    onActionSuccess: reloadHandler,
  };

  const openCreditInvoice = async () => {
    navigate(
      UrlHelper.formatPath(
        `/Invoice/Create?${RouteConstants.QueryParamStrings.InvoiceTemplate}=${props.invoice.id}&${RouteConstants.QueryParamStrings.IsCredit}=true`
      ),
      {
        state: { background: location },
      }
    );
  };

  const hasCreditInvoices = props.invoice.creditInvoiceIds?.length;

  const createCreditNoteAction: MoreActionMenuItemType = {
    description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.CreditAction),
    icon: IconDefinitionConstants.WStop,
    onClickHandler: hasCreditInvoices ? openCreditInvoice : undefined,
    actionDialog: hasCreditInvoices
      ? undefined
      : {
          isOpen: false,
          title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.CreditAction),
          subTitle: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.WhatDoYouWantToDo),
          titleIcon: IconDefinitionConstants.WStop,
          dialogType: DialogEnums.DialogType.Content,
          actions: [
            {
              text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Continue),
              persistOnSubmit: true,
            },
          ],
          maxWidth: "md",
          children: (
            <CreditInvoiceSelectionForm
              invoiceId={props.invoice.id}
              isTimeReportingInvoice={props.invoice.isTimeReportingInvoice}
              reloadInvoice={markAsDoneHandler ?? reloadHandler}
            />
          ),
        },
  };

  const copyInvoiceAction: MoreActionMenuItemType = {
    description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.CopyAction),
    icon: IconDefinitionConstants.WCopy,
    onClickHandler: async () => {
      navigate(
        UrlHelper.formatPath(`/Invoice/Create?${RouteConstants.QueryParamStrings.InvoiceTemplate}=${props.invoice.id}`),
        {
          state: { background: location },
        }
      );
    },
  };

  const { getAssignOwnerAction } = useAssignNewOwner(props.invoice.id, invoice.ownerId, reloadHandler);

  const canEditPayment = () => {
    const status = props.invoice.status;
    return (
      status === ApiModel.InvoiceStatus.Unpaid ||
      status === ApiModel.InvoiceStatus.OverdueReminderSent ||
      status === ApiModel.InvoiceStatus.OverdueReminderNotSent
    );
  };

  const canReportAsPaid = () => {
    const status = props.invoice.status;
    return (
      (status === ApiModel.InvoiceStatus.Unpaid ||
        status === ApiModel.InvoiceStatus.OverdueReminderSent ||
        status === ApiModel.InvoiceStatus.OverdueReminderNotSent ||
        status === ApiModel.InvoiceStatus.Collection) &&
      props.invoice.paymentDate === undefined
    );
  };

  const getMenuItems = (): MoreActionMenuItemType[] => {
    const status = invoice.status;
    const isSelfInvoice = invoice.invoiceSource === ApiModel.InvoiceSource.SelfInvoice;
    const actions: MoreActionMenuItemType[] = [];

    const isReadonlyUser = getIsReadonlyUser();
    if (!isReadonlyUser) {
      const isInvoiceAdmin = getIsInvoiceAdmin();
      if (
        isInvoiceAdmin &&
        !invoice.recurringId &&
        !invoice.credit &&
        !invoice.isReceiptRepaymentInvoice &&
        !isSelfInvoice
      ) {
        actions.push(createRecurringAction);
      }

      if (canEditPayment()) {
        actions.push(editPaymentAction);
      }

      if (canReportAsPaid()) actions.push(reportAsPaidAction);

      if (status !== ApiModel.InvoiceStatus.NotSent) {
        actions.push(downloadAction());
      }

      if (
        !props.invoice.credit &&
        !props.invoice.isReceiptRepaymentInvoice &&
        (status === ApiModel.InvoiceStatus.Unpaid ||
          status === ApiModel.InvoiceStatus.OverdueReminderSent ||
          status === ApiModel.InvoiceStatus.OverdueReminderNotSent ||
          status === ApiModel.InvoiceStatus.Collection)
      ) {
        createCreditNoteAction.icon.transform = cssExports.iconRotate90;
        actions.push(createCreditNoteAction);
      }

      if (!props.invoice.creditedInvoiceId && !props.invoice.isReceiptRepaymentInvoice) {
        actions.push(copyInvoiceAction);
      }

      if (status === ApiModel.InvoiceStatus.NotSent) {
        actions.push(snoozeAction);
        actions.push(cancelAction);
      }

      const assignNewOwnerAction = getAssignOwnerAction();
      if (assignNewOwnerAction) {
        actions.push(assignNewOwnerAction);
      }

      if (status !== ApiModel.InvoiceStatus.NotSent && status !== ApiModel.InvoiceStatus.Cancelled) {
        actions.push(ACTION_SEPARATOR);
        actions.push(LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.SendAsTitle));
        actions.push(emailInvoiceAdvancedAction());

        printInvoiceAction && actions.push(printInvoiceAction);
      }
    } else if (status !== ApiModel.InvoiceStatus.NotSent) {
      actions.push(downloadAction());
    }
    return actions;
  };

  return <MoreActions menuItems={getMenuItems()} disableSuccessMessages={disableSuccessMessages} />;
};

export default InvoiceMoreActions;
